<template>
  <div>
    <!-- Support FAB -->
    <v-btn
      fab
      fixed
      top
      right
      color="primary"
      elevation="4"
      class="support-fab"
      style="top: 80px; right: 16px"
      @click="openSupport"
    >
      <v-icon x-large>mdi-help-circle-outline</v-icon>
    </v-btn>

    <!-- Support Dialog -->
    <v-dialog
      v-model="dialog"
      transition="dialog-transition"
      max-width="600px"
      scrollable
      :fullscreen="$vuetify.breakpoint.smAndDown"
    >
      <v-card class="support-panel d-flex flex-column">
        <!-- Header -->
        <v-card-title class="py-3 px-4" v-if="isAuthenticated && currentUser">
          <v-list-item>
            <v-list-item-avatar size="32">
              {{ getUserInitials(currentUser.name) }}
            </v-list-item-avatar>
            <v-list-item-content class="pa-0">
              <v-list-item-title class="text-body-2 font-weight-medium">
                {{ currentUser.name || "Usuario" }}
              </v-list-item-title>
              <v-list-item-subtitle class="text-caption">
                {{ currentUser.email }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-spacer></v-spacer>
            <v-menu offset-y left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="handleLogout">
                  <v-list-item-icon>
                    <v-icon small>mdi-logout</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Cerrar sesión</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-btn icon small @click="dialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-list-item>
          <v-divider></v-divider>
          <v-tabs icons-and-text dense grow v-if="!$vuetify.breakpoint.smAndDown">
            <v-tab
              @click="
                activeTab = 0;
                selectedTicket = null;
              "
            >
              <span>Mis PQR</span>
              <v-icon>mdi-comment-outline</v-icon>
            </v-tab>
            <v-tab @click="activeTab = 2" :disabled="!categories.length">
              <span>Nuevo PQR</span>
              <v-icon>mdi-comment-question-outline</v-icon>
            </v-tab>

            <v-tab @click="activeTab = 3">
              <span>AI-BOS</span>
              <v-icon>mdi-face-agent</v-icon>
            </v-tab>
          </v-tabs>
        </v-card-title>

        <!-- Auth Section -->
        <v-card-text
          v-if="!isAuthenticated"
          class="flex-grow-1 auth-gradient d-flex align-center justify-center"
        >
          <v-container class="text-center" style="max-width: 400px">
            <v-icon size="48" color="primary" class="mb-6">mdi-shield-account</v-icon>

            <v-card class="pa-6" elevation="2">
              <v-card-title class="justify-center pb-2">
                <span class="text-h5">Acceder</span>
              </v-card-title>

              <v-card-subtitle class="text-center pb-4">
                Inicia sesión para gestionar tus PQR
              </v-card-subtitle>

              <!-- Login Form -->
              <v-form @submit.prevent="handleLogin" ref="loginForm">
                <v-text-field
                  v-model="email"
                  label="Email"
                  type="email"
                  outlined
                  dense
                  :rules="[rules.required, rules.email]"
                  prepend-inner-icon="mdi-email"
                  class="mb-2"
                ></v-text-field>

                <v-text-field
                  v-model="password"
                  label="Contraseña"
                  type="password"
                  outlined
                  dense
                  :rules="[rules.required]"
                  prepend-inner-icon="mdi-lock"
                  class="mb-4"
                ></v-text-field>

                <v-btn
                  type="submit"
                  color="primary"
                  block
                  large
                  :loading="loading"
                  class="mb-3"
                >
                  Iniciar Sesión
                </v-btn>
              </v-form>

              <v-divider class="my-4"></v-divider>

              <v-btn text color="primary" block @click="showRegister = !showRegister">
                {{ showRegister ? "Ya tengo cuenta" : "Crear cuenta nueva" }}
              </v-btn>

              <!-- Register Form -->
              <v-expand-transition>
                <v-form
                  v-show="showRegister"
                  @submit.prevent="handleRegister"
                  class="mt-4"
                >
                  <v-text-field
                    v-model="name"
                    label="Nombre completo"
                    outlined
                    dense
                    :rules="[rules.required]"
                    prepend-inner-icon="mdi-account"
                    class="mb-2"
                  ></v-text-field>

                  <v-text-field
                    v-model="registerEmail"
                    label="Email"
                    type="email"
                    outlined
                    dense
                    :rules="[rules.required, rules.email]"
                    prepend-inner-icon="mdi-email"
                    class="mb-2"
                  ></v-text-field>

                  <v-text-field
                    v-model="registerPassword"
                    label="Contraseña"
                    type="password"
                    outlined
                    dense
                    :rules="[rules.required, rules.password]"
                    prepend-inner-icon="mdi-lock"
                    class="mb-2"
                  ></v-text-field>

                  <v-text-field
                    v-model="confirmPassword"
                    label="Confirmar contraseña"
                    type="password"
                    outlined
                    dense
                    :rules="[rules.required, rules.passwordMatch]"
                    prepend-inner-icon="mdi-lock-check"
                    class="mb-4"
                  ></v-text-field>

                  <v-btn type="submit" color="primary" block large :loading="loading">
                    Crear Cuenta
                  </v-btn>
                </v-form>
              </v-expand-transition>

              <v-alert v-if="error" type="error" text dense class="mt-4">
                {{ error }}
              </v-alert>
            </v-card>
          </v-container>
        </v-card-text>

        <!-- Authenticated Dashboard -->
        <v-card-text v-else>
          <!-- Tab Content -->
          <v-tabs-items v-model="activeTab" class="flex-grow-1">
            <!-- PQR Tab -->
            <v-tab-item class="fill-height">
              <!-- PQR List -->

              <!-- Loading State -->
              <v-skeleton-loader
                v-if="loadingTickets"
                type="list-item-avatar-three-line@3"
                class="mx-4"
              ></v-skeleton-loader>

              <!-- Empty State -->
              <v-card
                flat
                class="fill-height d-flex flex-column"
                v-else-if="tickets.length === 0"
              >
                <v-card-text class="flex-grow-1 d-flex align-center justify-center">
                  <v-container class="text-center">
                    <v-icon size="64" color="grey lighten-2">mdi-inbox</v-icon>
                    <v-card-title class="justify-center">No tienes PQR</v-card-title>
                    <v-card-subtitle class="text-center">
                      Crea tu primera solicitud de soporte
                    </v-card-subtitle>
                    <v-btn color="primary" class="mt-3">
                      <v-icon left>mdi-plus</v-icon>
                      Nueva PQR
                    </v-btn>
                  </v-container>
                </v-card-text>
              </v-card>
              <!-- PQR List -->
              <v-list v-else class="flex-grow-1 py-0" style="overflow-y: auto">
                <v-list-item
                  v-for="ticket in tickets"
                  :key="ticket.id"
                  @click="selectTicket(ticket.id)"
                  three-line
                  class="ticket-list-item"
                >
                  <v-list-item-avatar size="40" class="mr-3">
                    <v-avatar :color="getStatusColor(ticket.status)" size="40">
                      <v-icon v-if="ticket.status === 'closed'" color="white" small>
                        mdi-check
                      </v-icon>
                      <v-icon v-else-if="ticket.status === 'pending'" color="white" small>
                        mdi-clock-outline
                      </v-icon>
                      <v-icon v-else color="white" small>
                        mdi-comment-question-outline
                      </v-icon>
                    </v-avatar>
                  </v-list-item-avatar>

                  <v-list-item-content class="py-2">
                    <v-list-item-title class="text-body-2 font-weight-medium mb-1">
                      {{ ticket.title }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption text--secondary mb-1">
                      <span class="ticket-preview">
                        {{ truncateText(ticket.description, 80) }}
                      </span>
                    </v-list-item-subtitle>
                    <v-list-item-subtitle>
                      <v-row no-gutters align="center">
                        <v-col cols="auto">
                          <v-chip
                            :color="getPriorityColor(ticket.priority)"
                            x-small
                            outlined
                            class="mr-2"
                          >
                            <v-icon left x-small>mdi-flag</v-icon>
                            {{ getPriorityText(ticket.priority) }}
                          </v-chip>
                        </v-col>
                        <v-col cols="auto">
                          <v-chip
                            v-if="ticket.category_name"
                            x-small
                            outlined
                            class="mr-2"
                          >
                            <v-icon left x-small>mdi-tag</v-icon>
                            {{ ticket.category_name }}
                          </v-chip>
                        </v-col>
                        <v-col cols="auto">
                          <span class="text-caption text--disabled">
                            {{ formatDateRelative(ticket.created_at) }}
                          </span>
                        </v-col>
                      </v-row>
                    </v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action class="my-auto">
                    <v-row no-gutters align="center">
                      <v-col cols="auto">
                        <v-badge
                          v-if="ticket.unread_count > 0"
                          :content="ticket.unread_count"
                          color="primary"
                          inline
                          class="mr-2"
                        >
                        </v-badge>
                        <v-icon small color="grey lighten-1">mdi-chevron-right</v-icon>
                      </v-col>
                    </v-row>
                  </v-list-item-action>
                </v-list-item>
                <v-divider v-if="tickets.length > 0" class="mt-0"></v-divider>
              </v-list>
            </v-tab-item>

            <!-- PQR Details -->
            <v-tab-item class="fill-height">
              <v-card flat class="fill-height d-flex flex-column">
                <v-card-title class="pa-0">
                  <v-toolbar flat dense>
                    <v-chip
                      :color="getStatusColor(ticketDetails.status)"
                      small
                      text-color="white"
                      class="mr-2"
                    >
                      {{ getStatusText(ticketDetails.status) }}
                    </v-chip>
                    <v-chip :class="'priority-' + ticketDetails.priority" small outlined>
                      {{ getPriorityText(ticketDetails.priority) }}
                    </v-chip>
                    <v-spacer></v-spacer>
                    <v-btn
                      color="error"
                      outlined
                      small
                      @click="closeTicket"
                      v-if="
                        ticketDetails.status !== 'closed' &&
                        (currentUser.id == ticketDetails.user_id ||
                          currentUser.role === 'admin')
                      "
                    >
                      <v-icon left small>mdi-check</v-icon>
                      Cerrar PQR
                    </v-btn>
                  </v-toolbar>
                </v-card-title>

                <v-card-text class="flex-grow-1 pa-0" style="overflow-y: auto">
                  <!-- PQR Header -->
                  <v-card outlined class="mb-4">
                    <v-card-title class="text-h6">
                      {{ ticketDetails.title }}
                    </v-card-title>
                    <v-spacer></v-spacer>
                    <v-btn
                      icon
                      small
                      @click="showCreateComment = true"
                      v-if="ticketDetails.status !== 'closed'"
                    >
                      <v-icon>mdi-comment-plus-outline</v-icon>
                    </v-btn>
                    <v-card-subtitle class="py-3">
                      <v-row dense>
                        <v-col cols="12" sm="6">
                          <v-list-item class="pa-0" dense>
                            <v-list-item-icon class="mr-2">
                              <v-icon small>mdi-tag</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content class="pa-0">
                              <v-list-item-subtitle>
                                {{ ticketDetails.category_name || "Sin categoría" }}
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-col>
                        <v-col cols="12" sm="6">
                          <v-list-item class="pa-0" dense>
                            <v-list-item-icon class="mr-2">
                              <v-icon small>mdi-calendar</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content class="pa-0">
                              <v-list-item-subtitle>
                                {{ formatDate(ticketDetails.created_at) }}
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-col>
                      </v-row>
                    </v-card-subtitle>
                    <v-card-text>
                      <v-card-text class="pa-0 mb-3">
                        {{ ticketDetails.description }}
                      </v-card-text>
                    </v-card-text>
                  </v-card>
                  <v-icon left>mdi-comment-multiple</v-icon>
                  Conversación
                  <!-- Comments Section -->

                  <!-- Loading Comments -->
                  <v-skeleton-loader
                    v-if="loadingComments"
                    type="list-item-avatar-two-line@2"
                    class="mx-4"
                  ></v-skeleton-loader>

                  <!-- Comments List -->
                  <v-list v-else-if="Array.isArray(comments) && comments.length > 0">
                    <v-list-item
                      v-for="comment in comments"
                      :key="comment.id"
                      class="align-start"
                    >
                      <v-list-item-avatar>
                        <v-avatar size="28" color="grey lighten-2">
                          <span class="text-caption">
                            {{ getUserInitials(comment.user_name || "U") }}
                          </span>
                        </v-avatar>
                      </v-list-item-avatar>

                      <v-list-item-content>
                        <v-list-item-subtitle class="mb-1">
                          <span class="font-weight-medium">{{
                            comment.user_name || "Usuario"
                          }}</span>

                          <span class="text-caption ml-2">{{
                            formatDate(comment.created_at)
                          }}</span>
                        </v-list-item-subtitle>

                        {{ comment.content }}

                        <v-chip-group
                          v-if="comment.attachments && comment.attachments.length > 0"
                          class="mt-2"
                        >
                          <v-chip
                            v-for="attachment in comment.attachments"
                            :key="attachment.id"
                            x-small
                            outlined
                            @click="downloadAttachment(attachment)"
                          >
                            <v-icon left x-small>mdi-paperclip</v-icon>
                            {{ attachment.filename }}
                          </v-chip>
                        </v-chip-group>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                  <v-card outlined v-else>
                    <!-- Empty Comments -->
                    <v-card-text class="text-center py-8">
                      <v-icon size="48" color="grey lighten-2"
                        >mdi-comment-outline</v-icon
                      >
                      <v-card-subtitle class="text-center">
                        No hay comentarios aún
                      </v-card-subtitle>
                    </v-card-text>
                  </v-card>
                  <v-card flat>
                    <v-card-text class="pa-0">
                      <v-text-field
                        v-model="newComment"
                        placeholder="Escribe un comentario..."
                        outlined
                        auto-grow
                        rows="1"
                        counter="500"
                        :rules="[(v) => v.length <= 500 || 'Máximo 500 caracteres']"
                        :disabled="addingComment"
                        elevation="10"
                        hide-details="auto"
                        prepend-icon="mdi-paperclip"
                        append-icon="mdi-send"
                        @click:prepend="triggerFileInput"
                        @click:append="addComment"
                      >
                      </v-text-field>
                      <input
                        ref="fileInput"
                        type="file"
                        multiple
                        accept="image/*,application/pdf,.txt,.doc,.docx"
                        style="display: none"
                        @change="handleFileChange"
                      />
                      <div v-if="commentAttachments.length" class="attachment-list mb-3">
                        <v-chip
                          v-for="(file, index) in commentAttachments"
                          :key="index"
                          small
                          close
                          class="mr-2 mb-2"
                          @click:close="removeAttachment(index)"
                        >
                          {{ file.name }}
                        </v-chip>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <v-tab-item class="fill-height">
              <v-form @submit.prevent="createTicket" ref="createForm">
                <v-text-field
                  v-model="newTicket.title"
                  label="Asunto"
                  outlined
                  dense
                  :rules="[rules.required]"
                  prepend-inner-icon="mdi-format-title"
                  class="mb-3"
                ></v-text-field>

                <v-row>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="newTicket.category_id"
                      :items="categories"
                      item-text="name"
                      item-value="id"
                      label="Categoría"
                      outlined
                      dense
                      :rules="[rules.required]"
                      prepend-inner-icon="mdi-tag"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="newTicket.priority"
                      :items="priorities"
                      label="Prioridad"
                      outlined
                      dense
                      prepend-inner-icon="mdi-flag"
                    ></v-select>
                  </v-col>
                </v-row>

                <v-textarea
                  v-model="newTicket.description"
                  label="Descripción del problema"
                  outlined
                  rows="4"
                  :rules="[rules.required]"
                  prepend-inner-icon="mdi-text"
                  class="mb-3"
                ></v-textarea>

                <v-file-input
                  v-model="newTicket.attachments"
                  label="Archivos adjuntos (opcional)"
                  multiple
                  outlined
                  dense
                  accept="image/*,application/pdf,.txt,.doc,.docx"
                  show-size
                  prepend-inner-icon="mdi-paperclip"
                  class="mb-4"
                ></v-file-input>

                <v-row>
                  <v-col>
                    <v-btn
                      type="submit"
                      color="primary"
                      :loading="creatingTicket"
                      :disabled="!categories.length"
                    >
                      <v-icon left>mdi-send</v-icon>
                      Crear PQR
                    </v-btn>
                    <v-btn text @click="showCreateForm = false" class="ml-2">
                      Cancelar
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-tab-item>

            <!-- Chat Tab -->
            <v-tab-item class="fill-height">
              <v-card flat class="fill-height d-flex flex-column">
                <v-card-title class="py-3">
                  <v-icon left>mdi-robot-outline</v-icon>
                  <span class="text-h6">Chat de Soporte</span>
                  <v-spacer></v-spacer>
                  <v-btn v-if="conversationId" text small @click="startNewConversation">
                    <v-icon left small>mdi-refresh</v-icon>
                    Nueva
                  </v-btn>
                </v-card-title>

                <!-- Chat Messages -->
                <v-sheet
                  class="chat-messages flex-grow-1 pa-4"
                  color="grey lighten-5"
                  style="overflow-y: auto"
                  ref="chatMessages"
                >
                  <!-- Welcome State -->
                  <v-container
                    v-if="messages.length === 0 && !isInitializing"
                    class="fill-height d-flex align-center justify-center"
                  >
                    <v-card class="text-center pa-8" elevation="0" color="transparent">
                      <v-icon size="48" color="primary">mdi-robot-outline</v-icon>
                      <v-card-title class="justify-center">Chat de Soporte</v-card-title>
                      <v-card-subtitle class="text-center">
                        Habla con nuestro asistente inteligente
                      </v-card-subtitle>
                      <v-btn
                        outlined
                        color="primary"
                        @click="initializeChat"
                        class="mt-3"
                      >
                        Iniciar conversación
                      </v-btn>
                    </v-card>
                  </v-container>

                  <!-- Chat Messages -->
                  <v-row
                    v-for="(msg, i) in messages"
                    :key="i"
                    :class="[
                      'mb-3',
                      msg.role === 'user' ? 'justify-end' : 'justify-start',
                    ]"
                    no-gutters
                  >
                    <v-col cols="auto" class="d-flex">
                      <v-avatar
                        v-if="msg.role !== 'user'"
                        size="24"
                        color="grey lighten-1"
                        class="mr-2"
                      >
                        <v-icon small>mdi-robot</v-icon>
                      </v-avatar>

                      <v-card
                        :color="msg.role === 'user' ? 'primary' : 'white'"
                        :dark="msg.role === 'user'"
                        class="message-bubble pa-3"
                        elevation="1"
                      >
                        <v-card-text
                          class="pa-0 text-body-2 message-content"
                          v-html="formatMessage(msg.content)"
                        >
                        </v-card-text>
                      </v-card>

                      <v-avatar
                        v-if="msg.role === 'user'"
                        size="24"
                        color="primary"
                        class="ml-2"
                      >
                        <v-icon small>mdi-account</v-icon>
                      </v-avatar>
                    </v-col>
                  </v-row>

                  <!-- Typing Indicator -->
                  <v-row v-if="isTyping" justify="start" no-gutters>
                    <v-col cols="auto" class="d-flex">
                      <v-avatar size="24" color="grey lighten-1" class="mr-2">
                        <v-icon small>mdi-robot</v-icon>
                      </v-avatar>
                      <v-card color="white" class="pa-3" elevation="1">
                        <div class="typing-dots">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-sheet>

                <!-- Chat Input -->
                <v-card-actions class="pa-3">
                  <v-text-field
                    v-model="newMessage"
                    label="Escribe tu mensaje..."
                    outlined
                    dense
                    hide-details
                    @keyup.enter="sendMessage"
                    :disabled="isTyping"
                    class="mr-2"
                  ></v-text-field>
                  <v-btn
                    icon
                    color="primary"
                    @click="sendMessage"
                    :disabled="!newMessage.trim() || isTyping"
                  >
                    <v-icon>mdi-send</v-icon>
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>

        <v-card-actions
          class="pa-0"
          v-if="isAuthenticated && currentUser && $vuetify.breakpoint.smAndDown"
        >
          <v-bottom-navigation grow color="primary">
            <v-btn
              @click="
                activeTab = 0;
                selectedTicket = null;
              "
              :disabled="!categories.length"
            >
              <span>Mis PQR</span>
              <v-icon>mdi-comment-outline</v-icon>
            </v-btn>
            <v-btn @click="activeTab = 2" :disabled="!categories.length">
              <span>Nuevo PQR</span>
              <v-icon>mdi-comment-question-outline</v-icon>
            </v-btn>
            <v-btn @click="activeTab = 3">
              <span>AI-BOS</span>
              <v-icon>mdi-face-agent</v-icon>
            </v-btn>
          </v-bottom-navigation>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Notifications -->
    <v-snackbar v-model="showSuccess" color="success" top timeout="3000">
      {{ successMessage }}
    </v-snackbar>

    <v-snackbar v-model="showError" color="error" top timeout="5000">
      {{ error }}
    </v-snackbar>
  </div>
</template>

<script>
export default {
  name: "SupportChat",
  props: {
    apiUrl: {
      type: String,
      default: "https://ai-bos.co/mcp-tickets",
    },
  },
  data() {
    return {
      // UI State
      showCreateComment: false,
      dialog: false,
      activeTab: 0,
      loading: false,
      error: "",
      showError: false,
      successMessage: "",
      showSuccess: false,
      showCreateForm: false,
      showRegister: false,

      // Validation rules
      rules: {
        required: (v) => !!v || "Este campo es requerido",
        email: (v) => /.+@.+\..+/.test(v) || "Email inválido",
        password: (v) => v?.length >= 6 || "Mínimo 6 caracteres",
        passwordMatch: (v) =>
          v === this.registerPassword || "Las contraseñas no coinciden",
      },

      // Auth
      isAuthenticated: false,
      currentUser: null,
      authToken: null,
      email: "",
      password: "",
      name: "",
      registerEmail: "",
      registerPassword: "",
      confirmPassword: "",

      // Chat
      messages: [],
      newMessage: "",
      isTyping: false,
      conversationId: null,
      isInitializing: false,

      // Tickets/PQR
      tickets: [],
      loadingTickets: false,
      selectedTicket: null,
      ticketDetails: {},
      comments: [],
      loadingComments: false,
      newComment: "",
      commentAttachments: [],
      addingComment: false,

      // Create Ticket
      newTicket: {
        title: "",
        description: "",
        category_id: null,
        priority: "medium",
        attachments: [],
      },
      categories: [],
      priorities: [
        { text: "Baja", value: "low" },
        { text: "Media", value: "medium" },
        { text: "Alta", value: "high" },
      ],
      creatingTicket: false,
    };
  },

  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.mobile;
    },
  },

  mounted() {
    // Check saved auth
    const token = localStorage.getItem("support_token");
    const user = localStorage.getItem("support_user");
    const savedConversationId = localStorage.getItem("support_conversation_id");

    if (token && user) {
      this.authToken = token;
      this.currentUser = JSON.parse(user);
      this.isAuthenticated = true;
      if (savedConversationId) {
        this.conversationId = savedConversationId;
      }
    }
  },

  watch: {
    error(newVal) {
      if (newVal) this.showError = true;
    },

    dialog(newVal) {
      if (newVal && this.isAuthenticated) {
        this.loadInitialData();
      }
    },

    activeTab(newVal) {
      if (newVal === 0 && this.isAuthenticated) {
        if (!this.tickets.length) this.fetchTickets();
        if (!this.categories.length) this.fetchCategories();
      }
    },

    messages: {
      handler() {
        this.$nextTick(() => {
          this.scrollChatToBottom();
        });
      },
      deep: true,
    },
  },

  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFileChange(event) {
      const files = Array.from(event.target.files);
      this.commentAttachments = [...this.commentAttachments, ...files];
      this.$refs.fileInput.value = ""; // Reset input for re-selection
    },
    removeAttachment(index) {
      this.commentAttachments.splice(index, 1);
    },
    // addComment() {
    //   this.$emit("add-comment", {
    //     text: this.newComment,
    //     attachments: this.commentAttachments,
    //   });
    //   this.newComment = "";
    //   this.commentAttachments = [];
    // },
    truncateText(text, length) {
      if (!text) return "";
      return text.length > length ? text.slice(0, length) + "..." : text;
    },

    openSupport() {
      this.dialog = true;
    },

    closeSupport() {
      this.dialog = false;
      this.error = "";
      this.selectedTicket = null;
      this.showCreateForm = false;
      this.showRegister = false;
    },

    getUserInitials(name) {
      if (!name) return "U";
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    },

    // === Status & Priority Helpers ===
    getStatusClass(status) {
      return `status-${status}`;
    },

    getStatusText(status) {
      const texts = {
        open: "Abierta",
        pending: "Pendiente",
        closed: "Cerrada",
      };
      return texts[status] || status;
    },

    getPriorityText(priority) {
      const texts = {
        low: "Baja",
        medium: "Media",
        high: "Alta",
      };
      return texts[priority] || priority;
    },

    // === Status Color Helpers ===
    getStatusColor(status) {
      const colors = {
        open: "success",
        pending: "warning",
        closed: "grey",
      };
      return colors[status] || "grey";
    },

    getPriorityColor(priority) {
      const colors = {
        low: "blue",
        medium: "orange",
        high: "red",
      };
      return colors[priority] || "grey";
    },

    // === Date Helpers ===
    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleString("es-ES");
    },

    formatDateRelative(date) {
      if (!date) return "";
      const now = new Date();
      const past = new Date(date);
      const diffMs = now - past;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "Ahora";
      if (diffMins < 60) return `${diffMins}m`;
      if (diffHours < 24) return `${diffHours}h`;
      if (diffDays < 7) return `${diffDays}d`;
      return past.toLocaleDateString("es-ES");
    },

    // === Auth Methods ===
    async handleLogin() {
      this.error = "";
      if (!this.email || !this.password) {
        this.error = "Por favor completa todos los campos";
        return;
      }

      this.loading = true;
      try {
        const response = await fetch(`${this.apiUrl}/users/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: this.email,
            password: this.password,
          }),
        });

        const data = await response.json();
        if (response.ok && data.token) {
          this.authToken = data.token;
          this.currentUser = data.user;
          this.isAuthenticated = true;
          localStorage.setItem("support_token", data.token);
          localStorage.setItem("support_user", JSON.stringify(data.user));

          // Clear form
          this.email = "";
          this.password = "";

          // Load data
          this.loadInitialData();

          this.successMessage = `¡Bienvenido ${data.user.name}!`;
          this.showSuccess = true;
        } else {
          this.error = data.error || "Error al iniciar sesión";
        }
      } catch (err) {
        this.error = "Error de conexión";
      } finally {
        this.loading = false;
      }
    },

    async handleRegister() {
      this.error = "";
      if (
        !this.name ||
        !this.registerEmail ||
        !this.registerPassword ||
        !this.confirmPassword
      ) {
        this.error = "Por favor completa todos los campos";
        return;
      }
      if (this.registerPassword !== this.confirmPassword) {
        this.error = "Las contraseñas no coinciden";
        return;
      }

      this.loading = true;
      try {
        const response = await fetch(`${this.apiUrl}/users/register`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: this.name,
            email: this.registerEmail,
            password: this.registerPassword,
            role: "client",
          }),
        });

        const data = await response.json();
        if (response.ok) {
          // Clear form
          this.name = "";
          this.registerEmail = "";
          this.registerPassword = "";
          this.confirmPassword = "";
          this.showRegister = false;

          this.successMessage = "Cuenta creada exitosamente. Ya puedes iniciar sesión.";
          this.showSuccess = true;
        } else {
          this.error = data.error || "Error al crear la cuenta";
        }
      } catch (err) {
        this.error = "Error de conexión";
      } finally {
        this.loading = false;
      }
    },

    handleLogout() {
      this.isAuthenticated = false;
      this.currentUser = null;
      this.authToken = null;
      this.messages = [];
      this.conversationId = null;
      this.tickets = [];
      this.selectedTicket = null;
      this.ticketDetails = {};
      this.comments = [];
      this.categories = [];
      this.showCreateForm = false;
      this.activeTab = 0;

      localStorage.removeItem("support_token");
      localStorage.removeItem("support_user");
      localStorage.removeItem("support_conversation_id");

      this.successMessage = "Sesión cerrada correctamente";
      this.showSuccess = true;
    },

    // === Data Loading ===
    async loadInitialData() {
      await Promise.all([this.fetchCategories(), this.fetchTickets()]);
    },

    // === Ticket/PQR Methods ===
    async fetchTickets() {
      this.loadingTickets = true;

      try {
        const response = await fetch(`${this.apiUrl}/tickets`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: this.authToken,
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Error al cargar PQR");
        }

        this.tickets = await response.json();
      } catch (err) {
        this.error =
          err.message === "No autorizado"
            ? "Tu sesión ha expirado. Por favor, vuelve a iniciar sesión."
            : "Error al cargar PQR: " + err.message;

        if (err.message === "No autorizado") {
          setTimeout(() => this.handleLogout(), 2000);
        }
      } finally {
        this.loadingTickets = false;
      }
    },

    async fetchCategories() {
      try {
        const response = await fetch(`${this.apiUrl}/categories`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: this.authToken,
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Error al cargar categorías");
        }

        const categories = await response.json();
        this.categories = categories.filter((category) => category.is_active !== false);
      } catch (err) {
        console.error("Error loading categories:", err);
      }
    },

    async createTicket() {
      if (!this.$refs.createForm.validate()) {
        this.error = "Por favor completa todos los campos requeridos";
        return;
      }

      this.creatingTicket = true;
      this.error = "";

      try {
        const formData = new FormData();
        formData.append("title", this.newTicket.title);
        formData.append("description", this.newTicket.description);
        formData.append("category_id", this.newTicket.category_id);
        formData.append("priority", this.newTicket.priority);

        if (this.newTicket.attachments?.length) {
          this.newTicket.attachments.forEach((file, index) => {
            formData.append(`attachments[${index}]`, file);
          });
        }

        const response = await fetch(`${this.apiUrl}/tickets`, {
          method: "POST",
          headers: { Authorization: this.authToken },
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Error al crear la PQR");
        }

        // Reset form
        this.newTicket = {
          title: "",
          description: "",
          category_id: null,
          priority: "medium",
          attachments: [],
        };
        this.showCreateForm = false;
        this.$refs.createForm.resetValidation();

        // Refresh tickets
        await this.fetchTickets();

        this.successMessage = "PQR creada exitosamente";
        this.showSuccess = true;
      } catch (err) {
        this.error =
          err.message === "No autorizado"
            ? "Tu sesión ha expirado. Por favor, vuelve a iniciar sesión."
            : "Error al crear la PQR: " + err.message;

        if (err.message === "No autorizado") {
          setTimeout(() => this.handleLogout(), 2000);
        }
      } finally {
        this.creatingTicket = false;
      }
    },

    async selectTicket(ticketId) {
      this.activeTab = 1;
      this.selectedTicket = ticketId;
      this.loadingComments = true;
      this.comments = [];

      try {
        // Get ticket details
        const ticketResponse = await fetch(`${this.apiUrl}/tickets/${ticketId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: this.authToken,
          },
        });

        if (!ticketResponse.ok) {
          const errorData = await ticketResponse.json();
          throw new Error(errorData.error || "Error al cargar la PQR");
        }

        this.ticketDetails = await ticketResponse.json();

        // Get comments
        this.comments = await this.fetchComments(ticketId);
      } catch (err) {
        this.error =
          err.message === "No autorizado"
            ? "Tu sesión ha expirado. Por favor, vuelve a iniciar sesión."
            : "Error al cargar la PQR: " + err.message;

        if (err.message === "No autorizado") {
          setTimeout(() => this.handleLogout(), 2000);
        }
      } finally {
        this.loadingComments = false;
      }
    },

    async fetchComments(ticketId) {
      try {
        const response = await fetch(`${this.apiUrl}/tickets/${ticketId}/updates`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: this.authToken,
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Error al cargar comentarios");
        }

        const result = await response.json();
        return Array.isArray(result) ? result : [];
      } catch (err) {
        console.error("Error fetching comments:", err);
        return [];
      }
    },

    async addComment() {
      if (!this.newComment.trim()) {
        this.error = "El comentario no puede estar vacío";
        return;
      }

      this.addingComment = true;

      try {
        const formData = new FormData();
        formData.append("content", this.newComment);
        formData.append("type", "comment");
        formData.append("is_public", "true");

        if (this.commentAttachments?.length) {
          this.commentAttachments.forEach((file, index) => {
            formData.append(`attachments[${index}]`, file);
          });
        }

        const response = await fetch(
          `${this.apiUrl}/tickets/${this.selectedTicket}/updates`,
          {
            method: "POST",
            headers: { Authorization: this.authToken },
            body: formData,
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Error al agregar comentario");
        }

        // Clear form
        this.newComment = "";
        this.commentAttachments = [];

        // Reload comments
        this.comments = await this.fetchComments(this.selectedTicket);

        this.successMessage = "Comentario agregado exitosamente";
        this.showSuccess = true;
        this.showCreateComment = false;
      } catch (err) {
        this.error =
          err.message === "No autorizado"
            ? "Tu sesión ha expirado. Por favor, vuelve a iniciar sesión."
            : "Error al agregar comentario: " + err.message;

        if (err.message === "No autorizado") {
          setTimeout(() => this.handleLogout(), 2000);
        }
      } finally {
        this.addingComment = false;
      }
    },

    async closeTicket() {
      if (!confirm("¿Estás seguro de que quieres cerrar esta PQR?")) return;

      try {
        const response = await fetch(
          `${this.apiUrl}/tickets/${this.selectedTicket}/close`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: this.authToken,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Error al cerrar la PQR");
        }

        // Reload ticket details and tickets list
        await Promise.all([this.selectTicket(this.selectedTicket), this.fetchTickets()]);

        this.successMessage = "PQR cerrada exitosamente";
        this.showSuccess = true;
      } catch (err) {
        this.error =
          err.message === "No autorizado"
            ? "Tu sesión ha expirado. Por favor, vuelve a iniciar sesión."
            : "Error al cerrar la PQR: " + err.message;

        if (err.message === "No autorizado") {
          setTimeout(() => this.handleLogout(), 2000);
        }
      }
    },

    downloadAttachment(attachment) {
      const url = `/uploads/tickets/${attachment.filename}`;
      window.open(url, "_blank");
    },

    // === Chat Methods ===
    async sendMessage() {
      if (!this.newMessage.trim()) return;

      const userMessage = { role: "user", content: this.newMessage };
      this.messages.push(userMessage);
      const messageContent = this.newMessage;
      this.newMessage = "";
      this.isTyping = true;

      try {
        const response = await fetch(`${this.apiUrl}/claude-agent/message`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: this.authToken,
          },
          body: JSON.stringify({
            content: messageContent,
            conversation_id: this.conversationId,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Error al enviar mensaje");
        }

        const data = await response.json();

        if (!this.conversationId) {
          this.conversationId = data.conversation_id;
          localStorage.setItem("support_conversation_id", this.conversationId);
        }

        this.messages.push({ role: "assistant", content: data.message });
      } catch (err) {
        this.messages.push({
          role: "assistant",
          content:
            err.message === "No autorizado"
              ? "Tu sesión ha expirado. Por favor, vuelve a iniciar sesión."
              : "Error: " + err.message,
        });

        if (err.message === "No autorizado") {
          setTimeout(() => this.handleLogout(), 2000);
        }
      } finally {
        this.isTyping = false;
      }
    },

    async initializeChat() {
      this.isInitializing = true;

      const contextMessage = `CONTEXTO DEL SISTEMA:
Usuario conectado:
- ID: ${this.currentUser.id}
- Nombre: ${this.currentUser.name}
- Email: ${this.currentUser.email}
- Rol: ${this.currentUser.role}

Por favor, saluda al usuario por su nombre y ofrece ayuda personalizada. Verifica si tiene PQR pendientes y menciónalo si es relevante.`;

      try {
        const response = await fetch(`${this.apiUrl}/claude-agent/message`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: this.authToken,
          },
          body: JSON.stringify({
            content: contextMessage,
            conversation_id: this.conversationId,
          }),
        });

        if (response.ok) {
          const data = await response.json();

          if (!this.conversationId) {
            this.conversationId = data.conversation_id;
            localStorage.setItem("support_conversation_id", this.conversationId);
          }

          this.messages.push({ role: "assistant", content: data.message });
        }
      } catch (err) {
        this.messages.push({
          role: "assistant",
          content: `¡Hola ${this.currentUser.name}! Soy Claude, tu asistente de soporte. ¿En qué puedo ayudarte hoy?`,
        });
      } finally {
        this.isInitializing = false;
      }
    },

    startNewConversation() {
      if (
        confirm(
          "¿Estás seguro de que quieres iniciar una nueva conversación? Se perderá el contexto actual."
        )
      ) {
        this.messages = [];
        this.conversationId = null;
        localStorage.removeItem("support_conversation_id");
        this.initializeChat();
      }
    },

    scrollChatToBottom() {
      const container = this.$refs.chatMessages;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },

    // === Message Formatting ===
    formatMessage(text) {
      if (!text) return "";
      let formatted = text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

      // Code blocks
      formatted = formatted.replace(
        /```([\s\S]*?)```/g,
        '<pre class="code-block"><code>$1</code></pre>'
      );

      // Inline code
      formatted = formatted.replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>');

      // Bold
      formatted = formatted.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
      formatted = formatted.replace(/__([^_]+)__/g, "<strong>$1</strong>");

      // Italic
      formatted = formatted.replace(/\*([^*]+)\*/g, "<em>$1</em>");
      formatted = formatted.replace(/_([^_]+)_/g, "<em>$1</em>");

      // Headers
      formatted = formatted.replace(/^### (.+)$/gm, "<h3>$1</h3>");
      formatted = formatted.replace(/^## (.+)$/gm, "<h2>$1</h2>");
      formatted = formatted.replace(/^# (.+)$/gm, "<h1>$1</h1>");

      // Lists
      formatted = formatted.replace(/^\* (.+)$/gm, "<li>$1</li>");
      formatted = formatted.replace(/^- (.+)$/gm, "<li>$1</li>");
      formatted = formatted.replace(/(<li>.*<\/li>)/s, "<ul>$1</ul>");

      // Numbered lists
      formatted = formatted.replace(/^\d+\. (.+)$/gm, '<li class="numbered">$1</li>');
      formatted = formatted.replace(/(<li class="numbered">.*<\/li>)/s, "<ol>$1</ol>");

      // Links
      formatted = formatted.replace(
        /\[([^\]]+)\]\(([^)]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener">$1</a>'
      );

      // Line breaks
      formatted = formatted.replace(/\n\n/g, "</p><p>");
      formatted = formatted.replace(/\n/g, "<br>");

      if (!formatted.match(/^<[h|p|ul|ol|pre]/)) {
        formatted = "<p>" + formatted + "</p>";
      }

      return formatted;
    },
  },
};
</script>

<style scoped>
/* === FAB Button === */
.support-fab {
  bottom: 24px !important;
  right: 24px !important;
}

@media (max-width: 600px) {
  .support-fab {
    bottom: 16px !important;
    right: 16px !important;
  }
}

/* === Panel Container === */
.support-panel {
  height: 80vh;
  max-height: 700px;
  min-height: 500px;
}

@media (max-width: 959px) {
  .support-panel {
    height: 100vh !important;
    max-height: none !important;
    border-radius: 0 !important;
  }
}

/* === Auth Background === */
.auth-gradient {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 400px;
}

/* === User Avatar === */
.user-avatar {
  width: 32px;
  height: 32px;
  font-size: 12px;
  font-weight: 600;
}

/* === Status Indicators === */
.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 12px;
}

.status-open {
  background-color: #4caf50;
}
.status-pending {
  background-color: #ff9800;
}
.status-closed {
  background-color: #9e9e9e;
}

/* === Priority Tags === */
.priority-low {
  color: #2196f3;
}
.priority-medium {
  color: #ff9800;
}
.priority-high {
  color: #f44336;
}

/* === Chat Messages === */
.chat-messages {
  background-color: #fafafa;
  max-height: 400px;
}

.message-bubble {
  max-width: 75%;
  border-radius: 16px;
  margin-bottom: 8px;
}

.user-message {
  align-self: flex-end;
}

.bot-message {
  align-self: flex-start;
  background-color: white;
  border: 1px solid #e0e0e0;
}

/* === Typing Animation === */
.typing-dots {
  display: flex;
  gap: 3px;
  padding: 8px;
}

.typing-dots span {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: #666;
  animation: typing 1.4s infinite ease-in-out;
}

.typing-dots span:nth-child(1) {
  animation-delay: -0.32s;
}
.typing-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes typing {
  0%,
  80%,
  100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* === Skeleton Loading === */
.skeleton-item {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* === Custom Scrollbars === */
.custom-scroll::-webkit-scrollbar {
  width: 4px;
}

.custom-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scroll::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 2px;
}

/* === Message Content Formatting === */
.message-content code {
  background: rgba(0, 0, 0, 0.1);
  padding: 2px 4px;
  border-radius: 3px;
  font-size: 0.9em;
}

.message-content pre {
  background: #f5f5f5;
  padding: 8px;
  border-radius: 4px;
  overflow-x: auto;
  margin: 4px 0;
}

.message-content pre code {
  background: none;
  padding: 0;
}

/* === Responsive Helpers === */
@media (max-width: 600px) {
  .mobile-full {
    width: 100% !important;
  }

  .mobile-stack {
    flex-direction: column !important;
  }

  .mobile-hide {
    display: none !important;
  }
}

/* === Vuetify Overrides (mínimos) === */
.v-btn {
  text-transform: none !important;
  font-weight: 500 !important;
}

.v-card {
  border-radius: 8px !important;
}

.v-dialog {
  border-radius: 12px !important;
}

.v-dialog--fullscreen {
  border-radius: 0 !important;
}

.whatsapp-card {
  border-radius: 12px;
  background-color: #f0f2f5;
}

.close-btn:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.message-input {
  background-color: #ffffff;

  padding: 4px 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.message-input :deep(.v-input__slot) {
  background: transparent !important;
  box-shadow: none !important;
  min-height: 32px !important;
  padding: 0 !important;
}

.message-input :deep(.v-text-field__details) {
  padding: 4px 8px;
}

.message-input :deep(.v-counter) {
  font-size: 12px;
  color: #999;
}

.attach-icon {
  color: #54656f;
  margin-right: 8px;
  cursor: pointer;
  transition: color 0.2s;
}

.attach-icon:hover:not(.v-icon--disabled) {
  color: #25d366;
}

.attach-icon.v-icon--disabled {
  color: #aaa;
}

.send-icon {
  color: #d0d0d0;
  margin-left: 8px;
  cursor: default;
  transition: color 0.2s;
}

.send-icon-active {
  color: #25d366;
  cursor: pointer;
}

.send-icon-active:hover {
  color: #20b858;
}

.send-icon.v-icon--disabled:not(.send-icon-active) {
  color: #d0d0d0;
}

.attachment-list {
  display: flex;
  flex-wrap: wrap;
}

.v-chip {
  background-color: #e0e0e0;
  font-size: 12px;
}
</style>
